import type { ReactNode } from "react";
import {
  Alert02Icon,
  AlertCircleIcon,
  CentralIcon,
  CheckmarkCircle01Icon,
  Clock03Icon,
  InformationCircleIcon,
} from "~/components/icons/central-icons";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "~/components/ui/alert";
import type { GliderStatusTone } from "~/lib/glider-color-tokens";
import { cn } from "~/lib/utils";

type StatusTone = GliderStatusTone;

const toneClassName: Record<StatusTone, string> = {
  default: "text-foreground",
  info: "text-status-info-foreground",
  pending: "text-status-pending-foreground",
  success: "text-status-positive-foreground",
  warning: "text-status-warning-foreground",
  destructive: "text-status-negative-foreground",
};

const toneIcon: Record<StatusTone, ReactNode> = {
  default: <CentralIcon icon={InformationCircleIcon} className="size-4" />,
  info: <CentralIcon icon={InformationCircleIcon} className="size-4" />,
  pending: <CentralIcon icon={Clock03Icon} className="size-4" />,
  success: <CentralIcon icon={CheckmarkCircle01Icon} className="size-4" />,
  warning: <CentralIcon icon={Alert02Icon} className="size-4" />,
  destructive: <CentralIcon icon={AlertCircleIcon} className="size-4" />,
};

interface StatusAlertProps {
  /** Optional recovery action rendered in the alert's action slot. */
  action?: ReactNode;
  className?: string;
  description?: ReactNode;
  /** Override the tone icon, or pass null to omit it. */
  icon?: ReactNode;
  title?: ReactNode;
  tone?: StatusTone;
}

export function StatusAlert({
  action,
  className,
  description,
  title,
  tone = "default",
  icon = toneIcon[tone],
}: StatusAlertProps) {
  if (!action) {
    return (
      <Alert className={cn(toneClassName[tone], className)} variant="default">
        {icon}
        {title ? <AlertTitle>{title}</AlertTitle> : null}
        {description ? (
          <AlertDescription>{description}</AlertDescription>
        ) : null}
      </Alert>
    );
  }
  return (
    <Alert
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-3 has-data-[slot=alert-action]:pr-4",
        toneClassName[tone],
        className,
      )}
      variant="default"
    >
      <div className="flex min-w-0 flex-1 basis-48 items-start gap-2.5 [&>svg]:mt-0.5 [&>svg]:shrink-0">
        {icon}
        <div className="min-w-0 space-y-0.5">
          {title ? <AlertTitle>{title}</AlertTitle> : null}
          {description ? (
            <AlertDescription>{description}</AlertDescription>
          ) : null}
        </div>
      </div>
      <AlertAction className="static ml-auto max-w-full">{action}</AlertAction>
    </Alert>
  );
}
